'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Bell, Mail, Zap, CheckCircle2, Info } from 'lucide-react';
import { toast } from 'sonner';

interface AlertFormValues {
  email: string;
  ccEmail: string;
  thresholdPct: number;
  frequency: 'immediate' | 'daily_digest' | 'weekly_digest';
  minImpressions: number;
  includeTopQuery: boolean;
  includeGscLink: boolean;
}

const frequencyOptions = [
  { id: 'immediate', label: 'Immediate', description: 'Email within 10 min of detection' },
  { id: 'daily_digest', label: 'Daily Digest', description: '8 AM summary of all spikes' },
  { id: 'weekly_digest', label: 'Weekly Digest', description: 'Monday morning roundup' },
];

const recentAlerts = [
  { id: 'al-001', date: 'Apr 6, 11:42 AM', query: 'acme', magnitude: 38, status: 'sent' as const },
  { id: 'al-002', date: 'Mar 28, 2:17 PM', query: 'acme', magnitude: 83, status: 'sent' as const },
  { id: 'al-003', date: 'Feb 26, 9:05 AM', query: 'acme', magnitude: 61, status: 'sent' as const },
  { id: 'al-004', date: 'Jan 27, 3:44 PM', query: 'acme review', magnitude: 221, status: 'sent' as const },
];

export default function AlertConfigPanel() {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [thresholdValue, setThresholdValue] = useState(40);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<AlertFormValues>({
    defaultValues: {
      email: '',
      ccEmail: '',
      thresholdPct: 40,
      frequency: 'immediate',
      minImpressions: 500,
      includeTopQuery: true,
      includeGscLink: true,
    },
  });

  const watchFrequency = watch('frequency');
  const watchIncludeTopQuery = watch('includeTopQuery');
  const watchIncludeGscLink = watch('includeGscLink');

  const onSubmit = (data: AlertFormValues) => {
    setSaving(true);
    try {
      localStorage.setItem('gsc_alert_config', JSON.stringify(data));
    } catch {}
    setTimeout(() => {
      setSaving(false);
      setSaved(true);
      toast.success('Alert configuration saved', {
        description: `Spike alerts set to ${data.thresholdPct}% threshold — emails go to ${data.email}`,
      });
      setTimeout(() => setSaved(false), 3000);
    }, 600);
  };

  const handleTestAlert = async () => {
    const email = watch('email');
    if (!email) {
      toast.error('Enter an email address first');
      return;
    }
    toast.promise(
      fetch('/api/gsc/alert', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          ccEmail: watch('ccEmail'),
          spikeDate: new Date().toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }),
          query: 'your-brand (test)',
          impressions: 4200,
          baseline: 3000,
          aboveBaseline: 40,
          siteUrl: 'your-site.com',
        }),
      }).then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error ?? 'Failed');
        return data;
      }),
      {
        loading: 'Sending test alert…',
        success: `Test alert sent to ${email}`,
        error: (err: Error) => `Test alert failed: ${err.message}`,
      }
    );
  };

  return (
    <div className="bg-white border border-[#e7e7e7] rounded-[5px] shadow-[rgba(150,25,50,0.04)_0px_4px_12px] overflow-hidden flex flex-col">
      {/* Header */}
      <div
        className="flex items-center gap-2 px-5 py-4 border-b border-[#e7e7e7]"
        style={{ backgroundColor: 'rgba(150, 25, 50, 0.04)' }}
      >
        {/* Smarty crimson icon wrapper */}
        <div
          className="w-[34px] h-[34px] rounded-[5px] flex items-center justify-center"
          style={{ backgroundColor: 'rgb(140, 11, 49)' }}
        >
          <Bell size={15} className="text-white" />
        </div>
        <h2 className="text-[15px] font-600 text-[#39364E]">Alert Configuration</h2>
        {isDirty && (
          <span
            className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-600"
            style={{
              backgroundColor: 'rgba(150, 25, 50, 0.08)',
              color: '#961932',
              border: '1px solid rgba(150, 25, 50, 0.2)',
            }}
          >
            Unsaved changes
          </span>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto scrollbar-thin">
        <div className="px-5 py-4 space-y-5">
          {/* Email */}
          <div>
            <label className="block text-xs font-600 text-[#39364E] mb-1">
              Alert Email <span className="text-[#961932]">*</span>
            </label>
            <p className="text-[11px] text-[#696969] mb-2">
              Spike notifications will be sent to this address
            </p>
            <div className="relative">
              <Mail
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#696969]"
              />
              <input
                type="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Enter a valid email',
                  },
                })}
                className="w-full pl-9 pr-3 py-2 text-sm border border-[#e7e7e7] rounded-[5px] bg-white text-[#39364E] focus:outline-none focus:ring-2 focus:ring-[rgba(150,25,50,0.3)] focus:border-[#961932] transition-all duration-150"
                placeholder="you@company.com"
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-[11px] text-red-500">{errors.email.message}</p>
            )}
          </div>

          {/* CC Email */}
          <div>
            <label className="block text-xs font-600 text-[#39364E] mb-1">
              CC Email{' '}
              <span className="text-[#696969] font-400">(optional)</span>
            </label>
            <div className="relative">
              <Mail
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#696969]"
              />
              <input
                type="email"
                {...register('ccEmail', {
                  pattern: {
                    value: /^$|^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Enter a valid email',
                  },
                })}
                className="w-full pl-9 pr-3 py-2 text-sm border border-[#e7e7e7] rounded-[5px] bg-white text-[#39364E] focus:outline-none focus:ring-2 focus:ring-[rgba(150,25,50,0.3)] focus:border-[#961932] transition-all duration-150"
                placeholder="team@company.com"
              />
            </div>
            {errors.ccEmail && (
              <p className="mt-1 text-[11px] text-red-500">{errors.ccEmail.message}</p>
            )}
          </div>

          {/* Spike Threshold */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-600 text-[#39364E]">Spike Threshold</label>
              <span className="text-sm font-700 tabular-nums text-[#961932]">+{thresholdValue}%</span>
            </div>
            <p className="text-[11px] text-[#696969] mb-3">
              Alert when branded impressions exceed this % above 30-day baseline
            </p>
            <input
              type="range"
              min={10}
              max={200}
              step={5}
              value={thresholdValue}
              onChange={(e) => {
                const v = Number(e.target.value);
                setThresholdValue(v);
                setValue('thresholdPct', v, { shouldDirty: true });
              }}
              className="w-full h-2 rounded-full appearance-none cursor-pointer"
              style={{
                accentColor: '#961932',
                background: `linear-gradient(to right, #961932 0%, #961932 ${
                  ((thresholdValue - 10) / 190) * 100
                }%, #e7e7e7 ${
                  ((thresholdValue - 10) / 190) * 100
                }%, #e7e7e7 100%)`,
              }}
            />
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-[#696969]">10% (sensitive)</span>
              <span className="text-[10px] text-[#696969]">200% (major only)</span>
            </div>
            {thresholdValue <= 20 && (
              <div
                className="flex items-start gap-1.5 mt-2 px-2.5 py-2 rounded-[5px]"
                style={{
                  backgroundColor: 'rgba(150, 25, 50, 0.06)',
                  border: '1px solid rgba(150, 25, 50, 0.2)',
                }}
              >
                <Info size={12} className="text-[#961932] flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-[#961932]">
                  Low threshold may generate many alerts on normal traffic variance
                </p>
              </div>
            )}
          </div>

          {/* Min Impressions */}
          <div>
            <label className="block text-xs font-600 text-[#39364E] mb-1">
              Minimum Impressions
            </label>
            <p className="text-[11px] text-[#696969] mb-2">
              Only alert on spikes with at least this many daily impressions
            </p>
            <input
              type="number"
              {...register('minImpressions', {
                min: { value: 100, message: 'Minimum 100 impressions' },
                max: { value: 50000, message: 'Maximum 50,000 impressions' },
              })}
              className="w-full px-3 py-2 text-sm border border-[#e7e7e7] rounded-[5px] bg-white text-[#39364E] font-mono focus:outline-none focus:ring-2 focus:ring-[rgba(150,25,50,0.3)] focus:border-[#961932] transition-all duration-150"
              placeholder="500"
            />
            {errors.minImpressions && (
              <p className="mt-1 text-[11px] text-red-500">{errors.minImpressions.message}</p>
            )}
          </div>

          {/* Frequency */}
          <div>
            <label className="block text-xs font-600 text-[#39364E] mb-1">
              Notification Frequency
            </label>
            <p className="text-[11px] text-[#696969] mb-3">
              How often to receive spike alert emails
            </p>
            <div className="space-y-2">
              {frequencyOptions.map((opt) => (
                <label
                  key={`freq-${opt.id}`}
                  className={`flex items-start gap-3 p-3 rounded-[5px] border cursor-pointer transition-all duration-150 ${
                    watchFrequency === opt.id
                      ? 'border-[#961932] bg-[rgba(150,25,50,0.04)]'
                      : 'border-[#e7e7e7] hover:border-[rgba(150,25,50,0.3)] hover:bg-[rgba(150,25,50,0.02)]'
                  }`}
                >
                  <input
                    type="radio"
                    value={opt.id}
                    {...register('frequency')}
                    className="mt-0.5"
                    style={{ accentColor: '#961932' }}
                  />
                  <div>
                    <p
                      className={`text-xs font-600 ${
                        watchFrequency === opt.id ? 'text-[#961932]' : 'text-[#39364E]'
                      }`}
                    >
                      {opt.label}
                    </p>
                    <p className="text-[11px] text-[#696969] mt-0.5">{opt.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Email Content Options */}
          <div>
            <label className="block text-xs font-600 text-[#39364E] mb-3">Email Content</label>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-500 text-[#39364E]">Include top spiking query</p>
                  <p className="text-[11px] text-[#696969]">
                    Show the highest-volume query in the alert
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setValue('includeTopQuery', !watchIncludeTopQuery, { shouldDirty: true })
                  }
                  className="relative w-10 h-5 rounded-full transition-colors duration-200 flex-shrink-0"
                  style={{ backgroundColor: watchIncludeTopQuery ? '#961932' : '#e7e7e7' }}
                  role="switch"
                  aria-checked={watchIncludeTopQuery}
                >
                  <div
                    className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${
                      watchIncludeTopQuery ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-500 text-[#39364E]">Include GSC deep link</p>
                  <p className="text-[11px] text-[#696969]">
                    Direct link to the query in Search Console
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setValue('includeGscLink', !watchIncludeGscLink, { shouldDirty: true })
                  }
                  className="relative w-10 h-5 rounded-full transition-colors duration-200 flex-shrink-0"
                  style={{ backgroundColor: watchIncludeGscLink ? '#961932' : '#e7e7e7' }}
                  role="switch"
                  aria-checked={watchIncludeGscLink}
                >
                  <div
                    className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${
                      watchIncludeGscLink ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Save Bar */}
        <div className="sticky bottom-0 px-5 py-4 bg-white border-t border-[#e7e7e7] space-y-2">
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 h-9 bg-[#961932] text-white rounded-[5px] text-sm font-600 hover:bg-[#8C0B31] active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed btn-smarty-shadow"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Saving…</span>
                </>
              ) : saved ? (
                <>
                  <CheckCircle2 size={14} />
                  <span>Saved</span>
                </>
              ) : (
                'Save Alert Config'
              )}
            </button>
            <button
              type="button"
              onClick={handleTestAlert}
              className="flex items-center gap-1.5 h-9 px-3 border border-[#e7e7e7] rounded-[5px] text-sm font-500 text-[#39364E] hover:bg-[rgba(150,25,50,0.04)] hover:border-[rgba(150,25,50,0.3)] active:scale-95 transition-all duration-150"
              title="Send a test alert to your email"
            >
              <Zap size={13} className="text-[#961932]" />
              Test
            </button>
          </div>
        </div>
      </form>

      {/* Recent Alerts */}
      <div
        className="border-t border-[#e7e7e7] px-5 py-4"
        style={{ backgroundColor: 'rgba(150, 25, 50, 0.02)' }}
      >
        <p className="text-xs font-600 text-[#39364E] mb-3">Recent Alerts Sent</p>
        <div className="space-y-2">
          {recentAlerts.map((alert) => (
            <div key={alert.id} className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[11px] font-500 text-[#39364E] truncate font-mono">
                    {alert.query}
                  </p>
                  <p className="text-[10px] text-[#696969]">{alert.date}</p>
                </div>
              </div>
              <span className="text-[11px] font-700 tabular-nums text-[#961932] flex-shrink-0">
                +{alert.magnitude}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}